import { useCallback, useEffect, useImperativeHandle, useMemo, useState, forwardRef } from "react";
import {
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonText,
} from "@ionic/react";
import { IExamWithQuestions } from "services/interfaces/IExam";
import type {
  IExamAnswerItem,
  IExamAnswerResult,
} from "services/interfaces/IExam";
import { usePostExamAnswers } from "hooks/examen/usePostExamAnswers";
import "./examQuestions.css";

export interface IAbandonDataRef {
  selectedAnswers: Record<number, number>;
  questions: { idExamQuestions: number }[];
  userId: number;
}

export interface IExamQuestionsHandle {
  submitAndFinish: () => Promise<boolean>;
}

interface IExamQuestionsProps {
  examQuestions: IExamWithQuestions;
  idBasis: string;
  idModule: string;
  idExam: string;
  userId: number;
  onFinish: (result: IExamAnswerResult) => void;
  abandonDataRef?: React.MutableRefObject<IAbandonDataRef | null>;
}

const ExamQuestionsInner: React.ForwardRefRenderFunction<
  IExamQuestionsHandle,
  IExamQuestionsProps
> = (
  {
    examQuestions,
    idBasis,
    idModule,
    idExam,
    userId,
    onFinish,
    abandonDataRef,
  },
  ref
) => {
  const { handlePostExamAnswers } = usePostExamAnswers();

  const questions = useMemo(
    () =>
      [...(examQuestions.questions ?? [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0),
      ),
    [examQuestions.questions],
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, number>
  >({});
  const [timeLeftSeconds, setTimeLeftSeconds] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const buildAnswersBody = useCallback((): IExamAnswerItem[] => {
    return questions
      .filter((q) => selectedAnswers[q.idExamQuestions] != null)
      .map((q) => ({
        idExamQuestions: q.idExamQuestions,
        idExamQuestionsAnswers: selectedAnswers[q.idExamQuestions],
      }));
  }, [questions, selectedAnswers]);

  useEffect(() => {
    if (abandonDataRef) {
      abandonDataRef.current = {
        selectedAnswers,
        questions: [...questions],
        userId,
      };
    }
    return () => {
      if (abandonDataRef) abandonDataRef.current = null;
    };
  }, [abandonDataRef, selectedAnswers, questions, userId]);

  useImperativeHandle(
    ref,
    () => ({
      submitAndFinish: async () => {
        if (isSubmitting) return false;
        setIsSubmitting(true);
        const answers = buildAnswersBody();
        const result = await handlePostExamAnswers(idBasis, idModule, idExam, {
          userId,
          answers,
        });
        setIsSubmitting(false);
        if (result) {
          onFinish(result);
          return true;
        }
        return false;
      },
    }),
    [
      isSubmitting,
      buildAnswersBody,
      handlePostExamAnswers,
      idBasis,
      idModule,
      idExam,
      userId,
      onFinish,
    ]
  );

  const durationMinutes = examQuestions.examDuration ?? 0;
  const totalSeconds = durationMinutes * 60;

  // Start timer when component mounts
  useEffect(() => {
    if (durationMinutes <= 0) return;
    setTimeLeftSeconds(totalSeconds);
  }, [durationMinutes, totalSeconds]);

  // Tick timer every second; submit when time runs out
  useEffect(() => {
    if (timeLeftSeconds == null || timeLeftSeconds <= 0 || isSubmitting) return;
    const interval = setInterval(() => {
      setTimeLeftSeconds((prev) => {
        if (prev == null || prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeftSeconds, isSubmitting]);

  useEffect(() => {
    if (timeLeftSeconds === 0 && !isSubmitting) {
      //   submitAnswers();
    }
  }, [timeLeftSeconds, isSubmitting]);

  const submitAnswers = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const answers = buildAnswersBody();
    const result = await handlePostExamAnswers(idBasis, idModule, idExam, {
      userId,
      answers,
    });
    setIsSubmitting(false);
    if (result) onFinish(result);
  }, [
    isSubmitting,
    buildAnswersBody,
    handlePostExamAnswers,
    idBasis,
    idModule,
    idExam,
    userId,
    onFinish,
  ]);

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const selectedForCurrent =
    currentQuestion && selectedAnswers[currentQuestion.idExamQuestions];

  const handleContinue = () => {
    if (isLastQuestion) {
      submitAnswers();
    } else {
      setCurrentIndex((i) => i + 1);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (!currentQuestion) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <IonText>No hay preguntas disponibles.</IonText>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <div className="exam-questions">
      {timeLeftSeconds != null && durationMinutes > 0 && (
        <div className="exam-questions-timer">
          <IonText color="warning">
            Tiempo restante: {formatTime(timeLeftSeconds)}
          </IonText>
        </div>
      )}

      <p className="exam-questions-progress">
        Pregunta {currentIndex + 1} de {questions.length}
      </p>

      <h2 className="exam-questions-title">{currentQuestion.questionText}</h2>

      <IonRadioGroup
        value={
          selectedForCurrent != null ? String(selectedForCurrent) : undefined
        }
        onIonChange={(e) => {
          const val = e.detail.value;
          if (val != null && currentQuestion) {
            setSelectedAnswers((prev) => ({
              ...prev,
              [currentQuestion.idExamQuestions]: Number(val),
            }));
          }
        }}
      >
        <IonList lines="full" className="exam-questions-list">
          {(currentQuestion.answers ?? []).map((answer) => (
            <IonItem
              key={answer.idExamQuestionsAnswers}
              className="exam-questions-item"
            >
              <IonRadio value={String(answer.idExamQuestionsAnswers)} />
              <IonLabel
                className="ion-text-wrap"
                style={{ marginLeft: "10px" }}
              >
                {answer.text}
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
      </IonRadioGroup>

      <IonButton
        expand="block"
        className="exam-questions-continue-btn"
        onClick={handleContinue}
        disabled={isSubmitting || selectedForCurrent == null}
      >
        {isSubmitting
          ? "Enviando..."
          : isLastQuestion
          ? "Finalizar examen"
          : "Continuar"}
      </IonButton>
    </div>
  );
};

const ExamQuestions = forwardRef(ExamQuestionsInner);
export default ExamQuestions;
